import { useLazyQuery, useMutation } from "@apollo/client/react";
import { ArrowLeftOutlined, ScanOutlined, UploadOutlined } from "@ant-design/icons";
import { css } from "@linaria/core";
import { App, Button, Empty, Flex, Image, Masonry, Space, Upload, type UploadFile, type UploadProps } from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { assert } from "ts-essentials";
import { DeletableImageFile } from "../../components/DeletableImageFile.tsx";
import { PhoneUploadModal } from "../../components/PhoneUploadModal.tsx";
import {
  CreateIngredientDocument,
  DeleteStagingFileDocument,
  GetStagingFileDocument,
  type GetStagingFileQuery,
  type IngredientFragment,
} from "../../gql/generated/graphqlSchema.tsx";
import { useUploadProgress } from "../../hooks/useUploadProgress.ts";
import { footerRow } from "../../styles/splitPaneSearch.styles.ts";
import { MAX_UPLOAD_SIZE_BYTES, uploadResponseSchema } from "../../utils/upload.ts";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp";

const contentArea = css`
  padding: var(--ant-padding-lg);
`;

type StagingFileItem = NonNullable<GetStagingFileQuery["stagingFile"]>;

export const CreateFromLabelView = ({
  onBack,
  onCreated,
}: {
  readonly onBack: () => void;
  readonly onCreated: (ingredient: IngredientFragment) => void;
}) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [createIngredient] = useMutation(CreateIngredientDocument);
  const [getStagingFile] = useLazyQuery(GetStagingFileDocument);
  const [deleteStagingFile] = useMutation(DeleteStagingFileDocument);

  const [stagedFiles, setStagedFiles] = useState<StagingFileItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { progress: uploadPercent, uploading, trackFile, completeFile, failFile } = useUploadProgress();

  const handleChange: UploadProps["onChange"] = useCallback(
    ({ file }: { file: UploadFile }) => {
      if (file.status === "uploading") {
        trackFile(file.uid, Math.round(file.percent ?? 0));
      }

      if (file.status === "done") {
        completeFile(file.uid);
        const parsed = uploadResponseSchema.safeParse(file.response);
        if (parsed.success) {
          for (const { stagingFileId } of parsed.data) {
            void getStagingFile({ variables: { id: stagingFileId } }).then(({ data }) => {
              const sf = data?.stagingFile;
              if (sf != null) {
                setStagedFiles((prev) => [...prev, sf]);
              }
            });
          }
        }
      }

      if (file.status === "error") {
        failFile(file.uid);
        void message.error(t("ingredients.photos.uploadFailed"));
      }
    },
    [trackFile, completeFile, failFile, getStagingFile, message, t],
  );

  const handleDelete = useCallback(
    (stagingFileId: string) => {
      void deleteStagingFile({ variables: { id: stagingFileId } });
      setStagedFiles((prev) => prev.filter((f) => f.id !== stagingFileId));
    },
    [deleteStagingFile],
  );

  const handlePhoneSelect = useCallback(
    (stagingFileId: string) => {
      void getStagingFile({ variables: { id: stagingFileId } }).then(({ data }) => {
        const sf = data?.stagingFile;
        if (sf != null) {
          setStagedFiles((prev) => [...prev, sf]);
        }
      });
    },
    [getStagingFile],
  );

  const renderPhoto = useCallback(
    (itemInfo: { data: StagingFileItem }) => (
      <DeletableImageFile file={itemInfo.data} onDelete={() => handleDelete(itemInfo.data.id)} />
    ),
    [handleDelete],
  );

  const handleCreate = async () => {
    if (submitting || stagedFiles.length === 0) return;
    setSubmitting(true);

    try {
      const { data } = await createIngredient({
        variables: {
          input: {
            name: t("ingredients.createChoice.newFromLabel"),
            stagingFileIds: stagedFiles.map((f) => f.id),
            recognizeLabel: true,
            language: i18n.language,
          },
        },
      });
      const created = data?.createIngredient;
      assert(created != null, "createIngredient returned no data");
      onCreated(created);
    } catch {
      // Apollo ErrorLink handles network errors globally
    } finally {
      setSubmitting(false);
    }
  };

  const uploadControls = (
    <Space.Compact>
      <Upload
        accept={ACCEPTED_TYPES}
        action="/i/upload/staging"
        beforeUpload={(file) => {
          if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            void message.error(t("ingredients.photos.tooLarge"));
            return Upload.LIST_IGNORE;
          }
          return true;
        }}
        multiple
        onChange={handleChange}
        pastable
        showUploadList={false}
      >
        <Button color="primary" icon={<UploadOutlined />} loading={uploading} variant="outlined">
          {uploading ? `${t("phone.uploading")} ${uploadPercent}%` : t("ingredients.photos.upload")}
        </Button>
      </Upload>
      <PhoneUploadModal onSelect={handlePhoneSelect} />
    </Space.Compact>
  );

  return (
    <>
      <Flex className={contentArea} gap="small" vertical>
        {stagedFiles.length === 0 ? (
          <Empty description={t("ingredients.createChoice.uploadHint")} styles={{ image: { marginBottom: 0 } }}>
            {uploadControls}
          </Empty>
        ) : (
          <>
            {uploadControls}
            <Image.PreviewGroup>
              <Masonry
                columns={{ xs: 2, sm: 3, md: 4 }}
                gutter={[8, 8]}
                itemRender={renderPhoto}
                items={stagedFiles.map((f) => ({ key: f.id, data: f }))}
              />
            </Image.PreviewGroup>
          </>
        )}
      </Flex>

      <div className={footerRow}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            {t("common.back")}
          </Button>
          <Button
            color="primary"
            disabled={stagedFiles.length === 0}
            icon={<ScanOutlined />}
            loading={submitting || uploading}
            onClick={() => void handleCreate()}
            variant="solid"
          >
            {t("common.create")}
          </Button>
        </Space>
      </div>
    </>
  );
};
